import React, { useState } from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { MAX_NUM_PROJECTS_PER_USER } from "@lifeat/shared/projects/ProjectsConstants";
import { ProjectValidationError, canUserCreateNewProject } from "@lifeat/shared/projects/ProjectsUtil";
import { PopUpMenuComponents } from "@common/LifeAtPopupMenuV2/PopUpMenuComponents";
import { LifeAtBodyPrimary } from "@common/LifeAtText/LifeAtText";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import PlusIcon from "@icons/Plus-Icon.svg";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { FilterItem } from "../../task-management/components/TaskTagFilterSidebarList";
import { ProjectCreationSource } from "../AmplitudeProjectsEvents";
import { projectList$ } from "../ProjectGlobals";
import ProjectCreationMenu from "./ProjectCreationMenu";

const DISABLE_CREATE_PROJECT_TOOLTIP = `You have reached the ${MAX_NUM_PROJECTS_PER_USER} projects limit, try deleting a project. Reach out to LifeAt to increase your limit!`;
const CREATE_PROJECT_LABEL = "new project";

const StyledPopupMenuTrigger = styled(PopUpMenuComponents.Trigger)`
    width: 100%;
`;
const StyledPlusIcon = styled(PlusIcon)`
    margin-right: 4px;
    stroke: ${(props) => props.theme.iconColor};
`;

interface CreateProjectButtonProps {
    readonly amplitudeSource: ProjectCreationSource;
    readonly className?: string;
}
export default function CreateProjectButton(props: CreateProjectButtonProps) {
    const { userData } = useUserContext();

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();

    const canUserCreateProjectResponse = canUserCreateNewProject(
        userData,
        Object.keys(projectList$.value || {}).length,
    );

    const handleCreateProject: React.MouseEventHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (canUserCreateProjectResponse.isValid === false) {
            e.preventDefault();
            if (canUserCreateProjectResponse.reason === ProjectValidationError.ProjectCreationDisallowed) {
                setUpgradeCtaContext("projects", UpgradeCTASource.PROJECTS);
            }
        }
    };

    return (
        <PopUpMenuComponents.Root open={isPopoverOpen} onOpenChange={(open: boolean) => setIsPopoverOpen(open)}>
            <StyledPopupMenuTrigger
                onClick={handleCreateProject}
                data-tip={DISABLE_CREATE_PROJECT_TOOLTIP}
                data-for={`project-creation-disabled`}
                className={props.className}
            >
                <FilterItem>
                    <StyledPlusIcon />
                    <LifeAtBodyPrimary>{CREATE_PROJECT_LABEL}</LifeAtBodyPrimary>
                    {canUserCreateProjectResponse.isValid === false &&
                        canUserCreateProjectResponse.reason === ProjectValidationError.MaxProjectsPerUser && (
                            <ReactTooltip id={`project-creation-disabled`} />
                        )}
                </FilterItem>
            </StyledPopupMenuTrigger>
            <PopUpMenuComponents.Content>
                <ProjectCreationMenu
                    handleClosePopup={() => setIsPopoverOpen(false)}
                    amplitudeSource={props.amplitudeSource}
                />
            </PopUpMenuComponents.Content>
        </PopUpMenuComponents.Root>
    );
}
